import catchify from 'catchify';

import {
  createTrackingLinkMetadata,
  getTrackingLinkByRid,
} from '../services/tracking-links';

// Rids already persisted this session so repeated hydration passes don't
// re-post the same metadata. Failures aren't cached, so they can be retried.
const syncedMetadataRids = new Set();

// Rids with a POST currently in flight, so overlapping hydration passes don't
// create duplicate metadata records for the same occurrence.
const pendingMetadataRids = new Set();

/**
 * Clear the in-memory cache of persisted metadata rids. Used by tests.
 */
export const resetTrackingLinkMetadataCache = () => {
  syncedMetadataRids.clear();
  pendingMetadataRids.clear();
};

/**
 * Generate a random rid for a TrackingLinkMetadata record. Generated
 * client-side so a QR code can render with the `r=` query param before the
 * metadata has round-tripped to the API.
 * @returns {String} Random metadata rid.
 */
export const generateTrackingLinkMetadataRid = () => (
  (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`)
    .replace(/-/g, '')
    .slice(0, 8)
);

/**
 * Extract TrackingLink fields from a source object. Products from the API can
 * carry the fields at the top level (flattened `UserProduct`) or nested under
 * `meta` (older/alternate payloads), so prefer whichever is present.
 * @param {Object} [source] - Product or fields object.
 * @returns {Object} `{ trackingLinkId, trackingLinkRid, trackingLink, trackingLinkMetadataRid }`.
 */
export const pickTrackingFields = (source = {}) => {
  const meta = source.meta || {};

  return {
    trackingLinkId: source.trackingLinkId ?? meta.trackingLinkId ?? null,
    trackingLinkRid: source.trackingLinkRid ?? meta.trackingLinkRid ?? null,
    trackingLink: source.trackingLink ?? meta.trackingLink ?? null,
    trackingLinkMetadataRid: source.trackingLinkMetadataRid ?? meta.trackingLinkMetadataRid ?? null,
  };
};

/**
 * Ensure tracking fields have a client-generated metadata rid.
 * Mutates `fields` when a rid is needed.
 * @param {Object} fields - Fields containing `trackingLinkRid`.
 * @returns {String|null} The metadata rid, if there is a tracking link.
 */
export const ensureTrackingLinkMetadataRid = (fields) => {
  const next = fields;

  if (!next?.trackingLinkRid) {
    return null;
  }

  if (!next.trackingLinkMetadataRid) {
    next.trackingLinkMetadataRid = generateTrackingLinkMetadataRid();
  }

  return next.trackingLinkMetadataRid;
};

/**
 * Recursively collect tracking field objects (objects with a `trackingLinkRid`)
 * from a timeline item's `fields` tree.
 * @param {Object} node - Fields object to walk.
 * @param {Array<Object>} [out] - Accumulator used during recursion.
 * @returns {Array<Object>} Tracking field objects.
 */
export const collectTrackingFields = (node, out = []) => {
  if (!node || typeof node !== 'object') {
    return out;
  }

  if (node.trackingLinkRid) {
    out.push(node);
  }

  Object.values(node).forEach((value) => {
    if (value && typeof value === 'object') {
      collectTrackingFields(value, out);
    }
  });

  return out;
};

/**
 * Persist a TrackingLinkMetadata record for tracking fields. Intended to be
 * fire-and-forget: the QR code can render with the client-generated rid even if
 * this request is still in flight or fails.
 *
 * If the API returns a different rid (e.g. the requested rid was already in use
 * by another account), `fields` is updated in place and `onRidResolved` is
 * invoked so callers can push the change on to outputs.
 *
 * @param {Object} fields - Tracking fields (with `trackingLinkRid`).
 * @param {Object} context - Metadata context.
 * @param {Number|String} context.productionId - Production the QR renders in.
 * @param {String} context.timelineId - Timeline item id.
 * @param {Number|String} [context.resourceId] - Graphic/theme/panel id.
 * @param {String} [context.resourceType] - Timeline item layout.
 * @param {String} [context.resourceSubType] - More specific resource type.
 * @param {String} [context.layout] - Layout name.
 * @param {Function} [context.onRidResolved] - Called when the persisted rid differs.
 * @returns {Promise<Object|null>} Created metadata, if successful.
 */
export const syncTrackingLinkMetadata = async (fields, context = {}) => {
  const next = fields;
  const rid = ensureTrackingLinkMetadataRid(next);

  if (!rid || !context.productionId) {
    return null;
  }

  if (syncedMetadataRids.has(rid) || pendingMetadataRids.has(rid)) {
    return null;
  }

  pendingMetadataRids.add(rid);

  try {
    let { trackingLinkId } = next;

    // Older graphics only store the rid, so resolve the numeric id on demand.
    if (!trackingLinkId) {
      const [lookupErr, link] = await catchify(getTrackingLinkByRid(next.trackingLinkRid));

      if (lookupErr || !link?.trackingLinkId) {
        console.warn('Unable to resolve tracking link for metadata sync', next.trackingLinkRid, lookupErr);
        return null;
      }

      trackingLinkId = link.trackingLinkId;
      next.trackingLinkId = trackingLinkId;
    }

    const [err, metadata] = await catchify(createTrackingLinkMetadata({
      trackingLinkId,
      rid,
      fields: {
        productionId: context.productionId,
        timelineId: context.timelineId,
        resourceId: context.resourceId,
        resourceType: context.resourceType,
        resourceSubType: context.resourceSubType,
        layout: context.layout || context.resourceType,
      },
    }));

    if (err) {
      console.warn('Failed to create tracking link metadata', err);
      return null;
    }

    syncedMetadataRids.add(metadata?.rid || rid);

    // The API generates a new rid when the requested one is already taken by
    // another account. Keep the timeline item pointed at the persisted rid.
    if (metadata?.rid && metadata.rid !== next.trackingLinkMetadataRid) {
      next.trackingLinkMetadataRid = metadata.rid;
      context.onRidResolved?.(next, metadata);
    }

    return metadata;
  } finally {
    pendingMetadataRids.delete(rid);
  }
};

export default {
  generateTrackingLinkMetadataRid,
  ensureTrackingLinkMetadataRid,
  pickTrackingFields,
  collectTrackingFields,
  syncTrackingLinkMetadata,
  resetTrackingLinkMetadataCache,
};
